/* MerryGlow — add to cart.

   The mg-main-product section renders its own plain <form data-product-form>
   instead of Horizon's <product-form-component>, so nothing in the theme was
   hearing about its adds. The cart icon, the cart drawer and the cart line
   items all refresh off the standard `shopify:cart:lines-update` event, so
   posting to /cart/add.js on its own left the bag looking untouched until the
   next page load.

   This owns the submit, dispatches that event around the request the same way
   product-form.js does, and opens the drawer on success. If this module fails
   to load the form still posts natively, so an add is never lost. */

import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const ADDING_LABEL = 'Adding…';
const SOURCE = 'mg-product-form';

/**
 * @param {string} name - Route name, e.g. `cart_add_url`.
 * @param {string} fallback
 * @returns {string}
 */
function cartRoute(name, fallback) {
  return window.Theme?.routes?.[name] ?? window.routes?.[name] ?? fallback;
}

/**
 * Sections Horizon needs re-rendered after the mutation. Read off the DOM rather
 * than hard-coded, so the drawer and the cart page each get their own back.
 * @returns {string}
 */
function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
    .filter(Boolean)
    .join(',');
}

/**
 * @returns {Promise<any>} The cart as the AJAX API sees it, for authoritative totals.
 */
function fetchCart() {
  return fetch(`${cartRoute('cart_url', '/cart')}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((response) => {
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  });
}

async function openCartDrawer() {
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (!drawer) return;

  // cart-drawer.js loads at low priority, so on a quick click the element may
  // not have upgraded yet — open() only exists once it has. Opening is
  // idempotent, so this is safe alongside the auto-open setting.
  await customElements.whenDefined('theme-drawer');
  /** @type {{ open?: () => void }} */ (drawer).open?.();
}

/**
 * The cart attribute that remembers a shopper turning the cover off.
 *
 * Stored on the cart rather than in localStorage so the choice survives a reload,
 * a second device on the same checkout, and a cleared browser — and so it is
 * legible in the Shopify admin if anyone ever has to explain a charge.
 */
const OPT_OUT_KEY = 'protection_optout';

/**
 * Reads the protection variant id published into the page by the cart snippet or
 * the layout config island. Returns null when no cover product is configured, in
 * which case every function here becomes a no-op.
 *
 * @returns {string | null}
 */
function coverVariantId() {
  const el = document.querySelector('[data-mg-cover-id]');
  const fromCart = el instanceof HTMLElement ? el.dataset.mgCoverId : null;
  if (fromCart) return fromCart;

  const cfg = document.getElementById('mg-cover-config');
  const fromCfg = cfg instanceof HTMLElement ? cfg.dataset.variantId : null;
  return fromCfg || null;
}

/**
 * @returns {Promise<{ attributes?: Record<string, string>, items?: Array<{ variant_id: number }> } | null>}
 */
async function readCart() {
  try {
    const res = await fetch(`${cartRoute('cart_url', '/cart')}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * Sets the cover line to an absolute quantity and records the shopper's intent.
 *
 * `/cart/update.js` takes an absolute quantity rather than a delta, so this is safe
 * to call repeatedly: adding a kit twice never stacks two covers, and clearing the
 * switch takes an existing cover back off.
 *
 * @param {string} id
 * @param {boolean} on
 */
async function setCover(id, on) {
  await fetch(`${cartRoute('cart_update_url', '/cart/update')}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      updates: { [id]: on ? 1 : 0 },
      attributes: { [OPT_OUT_KEY]: on ? '' : 'true' },
    }),
    credentials: 'same-origin',
  });
}

/**
 * Adds the cover alongside a bag unless the shopper has already declined it.
 *
 * Deliberately does nothing when the opt-out attribute is set: re-adding a charge
 * somebody has already removed is the behaviour that turns an add-on into a
 * complaint. A failure here is swallowed — the cover must never be the reason the
 * bag did not make it into the cart.
 */
async function syncProtection() {
  const id = coverVariantId();
  if (!id) return;

  try {
    const cart = await readCart();
    if (cart?.attributes?.[OPT_OUT_KEY] === 'true') return;
    await setCover(id, true);
  } catch {
    /* Leave the cover off and carry on with the bag. */
  }
}

/**
 * Wires the cart and drawer switches. Delegated from the document so it keeps
 * working after Horizon re-renders the cart sections.
 */
function wireCoverToggle() {
  document.addEventListener('change', async (event) => {
    const toggle = /** @type {HTMLInputElement | null} */ (
      event.target instanceof HTMLElement ? event.target.closest('[data-mg-cover-toggle]') : null
    );
    if (!toggle) return;

    const root = toggle.closest('[data-mg-cover]');
    const id = root instanceof HTMLElement ? root.dataset.mgCoverId : null;
    if (!id) return;

    const status = root?.querySelector('[data-mg-cover-status]');
    const wanted = toggle.checked;
    if (root instanceof HTMLElement) root.dataset.busy = 'true';

    try {
      await setCover(id, wanted);
      const cart = await fetchCart();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: wanted ? 'add' : 'remove',
          context: 'cart',
          lines: [],
          promise: Promise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, itemCount: cart.item_count, source: SOURCE, didError: false },
          }),
        })
      );
      if (status instanceof HTMLElement) status.hidden = true;
    } catch {
      // Put the switch back where the shopper found it and say so, rather than
      // leaving it showing a state the cart does not actually have.
      toggle.checked = !wanted;
      if (status instanceof HTMLElement) {
        status.textContent = 'That did not save. Try again.';
        status.hidden = false;
      }
    } finally {
      if (root instanceof HTMLElement) delete root.dataset.busy;
    }
  });
}

wireCoverToggle();

/**
 * @param {HTMLFormElement} form
 */
async function addToCart(form) {
  const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('[data-atc]'));
  const errorSlot = /** @type {HTMLElement | null} */ (form.querySelector('[data-atc-error]'));
  const label = button?.textContent ?? '';

  if (button) {
    button.disabled = true;
    button.textContent = ADDING_LABEL;
  }
  if (errorSlot) errorSlot.hidden = true;

  const formData = new FormData(form);
  const sections = cartSectionIds();
  if (sections) formData.append('sections', sections);

  const quantity = Number(formData.get('quantity')) || 1;
  const merchandiseId = String(formData.get('id') ?? '');
  const deferred = CartLinesUpdateEvent.createPromise();

  // Dispatched before the request so the icon and drawer can show their pending
  // state, then settled below. Bubbles from the form up to the document
  // listeners those components register.
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId, quantity }],
      promise: deferred.promise,
    })
  );

  try {
    await syncProtection();

    const response = await fetch(cartRoute('cart_add_url', '/cart/add'), {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
      credentials: 'same-origin',
    }).then((result) => result.json());

    if (response.status) {
      const error = new Error(response.description || response.message || 'Add to cart failed');
      /** @type {any} */ (error).cartErrorCode = 'INVALID';
      throw error;
    }

    const cart = await fetchCart();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: quantity,
        sections: response.sections,
        source: SOURCE,
        didError: false,
      },
    });

    await openCartDrawer();
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Add to cart failed';

    form.dispatchEvent(
      new CartErrorEvent({
        error: message,
        code: /** @type {any} */ (error)?.cartErrorCode ?? 'SERVICE_UNAVAILABLE',
      })
    );

    // Settle the update event either way — listeners hold this promise, and an
    // unsettled one leaves them stuck in their pending state for good.
    const cart = await fetchCart().catch(() => null);
    if (cart) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: quantity, source: SOURCE, didError: true },
      });
    } else {
      deferred.reject(error);
    }

    if (errorSlot) {
      errorSlot.textContent = message;
      errorSlot.hidden = false;
    }
  } finally {
    if (button) {
      button.disabled = false;
      button.textContent = label;
    }
  }
}

// Delegated, so the handler survives the theme editor re-rendering the section.
document.addEventListener('submit', (event) => {
  const target = /** @type {HTMLElement | null} */ (event.target);
  const form = /** @type {HTMLFormElement | null} */ (target?.closest?.('form[data-product-form]') ?? null);
  if (!form) return;

  event.preventDefault();
  addToCart(form);
});


/**
 * Sitewide minimum order value, re-checked in the browser.
 *
 * The cart summary already renders the button disabled below the minimum. This
 * keeps it correct after an AJAX cart change, where the button may be re-drawn
 * from a cached section or a quantity is edited without a page load.
 *
 * This is a usability guard, not a security control. Anyone who skips the cart
 * entirely can still reach /checkout, so the authoritative rule has to live in
 * a Shopify checkout-validation function. Both layers are wanted: this one
 * explains the rule to a real customer, the function stops an attacker.
 */
const MIN_ORDER_FALLBACK = 7000;

function minOrderCents() {
  const el = document.querySelector('[data-min-order]');
  const v = el ? parseInt(el.dataset.minOrder, 10) : NaN;
  return Number.isFinite(v) ? v : MIN_ORDER_FALLBACK;
}

function money(cents) {
  return '$' + (cents / 100).toFixed(2);
}

async function enforceMinimum() {
  const buttons = document.querySelectorAll('[data-checkout-btn], button[name="checkout"]');
  if (!buttons.length) return;

  let cart;
  try {
    cart = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((r) => r.json());
  } catch {
    return; // Never leave checkout blocked because a fetch failed.
  }
  if (!cart || !cart.item_count) return;

  const min = minOrderCents();
  const short = min - cart.total_price;
  const below = short > 0;

  buttons.forEach((b) => {
    b.disabled = below;
  });

  document.querySelectorAll('[data-min-note]').forEach((note) => {
    note.hidden = !below;
    if (below) {
      note.textContent =
        'Orders start at ' + money(min) + '. Add ' + money(short) + ' more to check out.';
    }
  });
}

document.addEventListener('DOMContentLoaded', enforceMinimum);
document.addEventListener('CartLinesUpdate', () => setTimeout(enforceMinimum, 400));
